import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileUp, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface DVLASectionProps {
  drivingLicenceNumber: string;
  setdrivingLicenceNumber: (value: string) => void;
  licenseFrontFile: File | null;
  setLicenseFrontFile: (file: File | null) => void;
  licenseFrontPreview: string | null;
  setLicenseFrontPreview: (preview: string | null) => void;
  licenseBackFile: File | null;
  setLicenseBackFile: (file: File | null) => void;
  licenseBackPreview: string | null;
  setLicenseBackPreview: (preview: string | null) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const DVLASection: React.FC<DVLASectionProps> = ({
  drivingLicenceNumber,
  setdrivingLicenceNumber,
  licenseFrontFile,
  setLicenseFrontFile,
  licenseFrontPreview,
  setLicenseFrontPreview,
  licenseBackFile,
  setLicenseBackFile,
  licenseBackPreview,
  setLicenseBackPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();
  const [dvlaConsentAccepted, setDvlaConsentAccepted] = useState(false);

  const handleLicenseFrontChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLicenseFrontFile(file);
      const fileUrl = URL.createObjectURL(file);
      setLicenseFrontPreview(fileUrl);
    }
  };

  const handleLicenseBackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLicenseBackFile(file);
      const fileUrl = URL.createObjectURL(file);
      setLicenseBackPreview(fileUrl);
    }
  };

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Allow previously uploaded images (preview URLs) to satisfy requirement
    const hasFront = Boolean(licenseFrontFile) || Boolean(licenseFrontPreview);
    const hasBack = Boolean(licenseBackFile) || Boolean(licenseBackPreview);

    // Separate validation toasts for clarity
    if (!drivingLicenceNumber) {
      toast({
        title: "Missing Driver License Number",
        description: "Please enter your driver license number.",
        variant: "destructive",
      });
      return;
    }

    if (!hasFront) {
      toast({
        title: "Front Photo Required",
        description: "Please attach the front of your driving license.",
        variant: "destructive",
      });
      return;
    }

    if (!hasBack) {
      toast({
        title: "Back Photo Required",
        description: "Please attach the back of your driving license.",
        variant: "destructive",
      });
      return;
    }

    // Check if exactly 16 characters
    if (drivingLicenceNumber.length !== 16) {
      toast({
        title: "Invalid Driver License Number",
        description: "Driver license number must be exactly 16 characters long",
        variant: "destructive",
      });
      return;
    }

    // Check if alphanumeric (letters and numbers only)
    if (!/^[a-zA-Z0-9]+$/.test(drivingLicenceNumber)) {
      toast({
        title: "Invalid Driver License Number",
        description:
          "Driver license number must contain only letters and numbers",
        variant: "destructive",
      });
      return;
    }

    if (!dvlaConsentAccepted) {
      toast({
        title: "Consent Required",
        description:
          "Please read and accept the Data Protection Declaration before submitting.",
        variant: "destructive",
      });
      return;
    }

    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>DVLA License Check</CardTitle>
        <CardDescription>
          Please provide both your driver license number and upload photos of
          your driving license
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="drivingLicenceNumber">Driver License Number</Label>
            <Input
              id="drivingLicenceNumber"
              value={drivingLicenceNumber}
              onChange={(e) => setdrivingLicenceNumber(e.target.value)}
              placeholder="Enter your driver license number"
              minLength={16}
              maxLength={16}
              required
            />
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="licenseFront">
                Front of Driving License Photo
              </Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => triggerFileInput("licenseFront")}
              >
                {licenseFrontPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <img
                      src={licenseFrontPreview}
                      alt="License front preview"
                      className="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLicenseFrontFile(null);
                        setLicenseFrontPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload front of license
                    </p>
                  </>
                )}
              </div>
              <Input
                id="licenseFront"
                name="licenseFront"
                type="file"
                className="hidden"
                onChange={handleLicenseFrontChange}
                accept="image/png,image/jpeg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="licenseBack">Back of Driving License Photo</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => triggerFileInput("licenseBack")}
              >
                {licenseBackPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <img
                      src={licenseBackPreview}
                      alt="License back preview"
                      className="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLicenseBackFile(null);
                        setLicenseBackPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload back of license
                    </p>
                  </>
                )}
              </div>
              <Input
                id="licenseBack"
                name="licenseBack"
                type="file"
                className="hidden"
                onChange={handleLicenseBackChange}
                accept="image/png,image/jpeg"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="border rounded-lg bg-gray-50 p-4 space-y-3">
              <h4 className="font-semibold text-sm text-gray-900">
                Data Protection Declaration
              </h4>
              <div className="text-sm text-gray-700 space-y-2 max-h-48 overflow-y-auto pr-2">
                <p>
                  HIVED LTD will request your driving licence data from the DVLA
                  for the purpose of verifying your driving licence records to
                  assess your suitability for employment as a delivery driver.
                </p>
                <p>
                  DVLA will disclose to HIVED LTD all relevant information held
                  in the computerised register of drivers maintained by DVLA.
                  This includes personal details, driving entitlements, valid
                  endorsements and disqualifications (if relevant), Certificate
                  of Professional Competence (CPC) and Digital Tachograph Card
                  details (where appropriate).
                </p>
                <p>
                  This declaration will expire when you cease driving in
                  connection with HIVED LTD or in any case, 3 years from the
                  date of submission.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="dvla-consent"
                checked={dvlaConsentAccepted}
                onCheckedChange={(checked) =>
                  setDvlaConsentAccepted(checked === true)
                }
              />
              <label
                htmlFor="dvla-consent"
                className="text-sm font-medium leading-tight cursor-pointer"
              >
                I confirm that I have read and understood the above Data
                Protection Declaration and consent to HIVED LTD requesting my
                driving licence data from the DVLA.
              </label>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white"
            disabled={isLoading || !dvlaConsentAccepted}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit DVLA Information"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default DVLASection;
